(() => {
  if (window.MaisonPdpHeroBootstrapped) return;
  window.MaisonPdpHeroBootstrapped = true;

  const instances = new Map();

  function init(root) {
    if (instances.has(root)) return;

    const mainElement = root.querySelector('[data-maison-main-gallery]');
    const thumbElement = root.querySelector('[data-maison-thumbs]');
    const thumbButtons = [...root.querySelectorAll('[data-maison-thumb]')];
    const slideElements = [...root.querySelectorAll('.maisonco-pdp-hero__main-swiper .swiper-slide')];
    let mainSwiper;
    let thumbSwiper;
    let testimonialSwiper;
    let lightbox;

    const leftColumn = root.querySelector(".maisonco-pdp-hero__left");
    const leftColumnSize = new ResizeObserver(([entry]) => {
      root.style.setProperty("--maisonco-left-height", `${entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height}px`);
    });
    if (leftColumn) leftColumnSize.observe(leftColumn);

    if (window.Swiper && mainElement && slideElements.length) {
      if (thumbElement && thumbButtons.length > 1) {
        thumbSwiper = new window.Swiper(thumbElement, {
          slidesPerView: 'auto',
          spaceBetween: 8,
          freeMode: true,
          watchSlidesProgress: true,
        });
      }

      mainSwiper = new window.Swiper(mainElement, {
        slidesPerView: 1,
        speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 350,
        navigation: {
          prevEl: root.querySelector('[data-maison-prev]'),
          nextEl: root.querySelector('[data-maison-next]'),
        },
        keyboard: { enabled: true, onlyInViewport: true },
        a11y: { enabled: true },
        on: {
          slideChange(swiper) {
            thumbButtons.forEach((button, index) => {
              button.setAttribute('aria-current', index === swiper.activeIndex ? 'true' : 'false');
            });
            thumbSwiper?.slideTo(swiper.activeIndex);
          },
        },
      });

      thumbButtons.forEach((button, index) => {
        button.setAttribute('aria-current', index === mainSwiper.activeIndex ? 'true' : 'false');
        button.addEventListener('click', () => mainSwiper.slideTo(index));
      });
    }

    const gallery = root.querySelector('[data-maison-zoom-gallery]');
    if (gallery?.querySelector('a') && root.dataset.pswpLightbox && root.dataset.pswpCore) {
      import(root.dataset.pswpLightbox).then(({ default: PhotoSwipeLightbox }) => {
        if (!root.isConnected) return;
        lightbox = new PhotoSwipeLightbox({
          gallery,
          children: 'a',
          pswpModule: () => import(root.dataset.pswpCore),
          showHideAnimationType: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'none' : 'zoom',
        });
        lightbox.init();
      }).catch((error) => console.error('Maison PDP Hero: PhotoSwipe could not load', error));
    }

    const variantSelect = root.querySelector('[data-maison-variant-select]');
    variantSelect?.addEventListener('change', () => {
      const variantInput = root.querySelector('product-form-component [name="id"]');
      if (variantInput) variantInput.value = variantSelect.value;
    });

    const videoEvents = new AbortController();
    const videoEventOptions = { signal: videoEvents.signal };
    const testimonialElement = root.querySelector('[data-maison-ugc-slider]');
    if (window.Swiper && testimonialElement) {
      testimonialSwiper = new window.Swiper(testimonialElement, {
        slidesPerView: 'auto',
        spaceBetween: 12,
        speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 350,
        watchOverflow: true,
        grabCursor: true,
        touchMoveStopPropagation: true,
        navigation: {
          prevEl: root.querySelector('[data-maison-ugc-prev]'),
          nextEl: root.querySelector('[data-maison-ugc-next]'),
        },
        a11y: { enabled: true },
        on: {
          sliderFirstMove: closeOpenVideos,
          init: updateVideoProgress,
          progress: updateVideoProgress,
          resize: updateVideoProgress,
          setTransition(swiper, duration) {
            root.querySelector('[data-maison-ugc-progress]')?.style.setProperty('--maisonco-ugc-progress-duration', `${duration}ms`);
          },
        },
      });
      testimonialElement.addEventListener('keydown', (event) => {
        if (event.target !== testimonialElement) return;
        const actions = {
          ArrowRight: () => testimonialSwiper.slideNext(),
          ArrowLeft: () => testimonialSwiper.slidePrev(),
          Home: () => testimonialSwiper.slideTo(0),
          End: () => testimonialSwiper.slideTo(testimonialSwiper.slides.length - 1),
        };
        if (!actions[event.key]) return;
        event.preventDefault();
        event.stopPropagation();
        closeOpenVideos();
        actions[event.key]();
      }, videoEventOptions);
    }

    function closeOpenVideos() {
      root.querySelectorAll('[data-maison-video][open]').forEach((details) => closeVideo(details));
    }

    const videoVisibility = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) target.pause();
      });
    });

    function updateVideoProgress(swiper) {
      const progress = 25 + Math.min(1, Math.max(0, swiper.progress || 0)) * 75;
      const bar = root.querySelector('[data-maison-ugc-progress]');
      if (!bar) return;
      bar.style.setProperty('--maisonco-ugc-progress', `${progress}%`);
      bar.setAttribute('aria-valuenow', String(Math.round(progress)));
    }

    function closeVideo(details, restoreFocus = false) {
      details.querySelector('video')?.pause();
      details.open = false;
      if (restoreFocus) details.querySelector('summary')?.focus({ preventScroll: true });
    }

    function openVideo(details) {
      root.querySelectorAll('[data-maison-video][open]').forEach((other) => {
        if (other !== details) closeVideo(other);
      });
      details.open = true;
      const template = details.querySelector('template');
      if (template) {
        template.replaceWith(template.content.cloneNode(true));
        const player = details.querySelector('video');
        player.addEventListener('error', () => {
          details.querySelector('[role="status"]').hidden = false;
        }, videoEventOptions);
        videoVisibility.observe(player);
      }
      const video = details.querySelector('video');
      details.querySelector('[role="status"]').hidden = true;
      if (video.error) video.load();
      video.playsInline = true;
      video.muted = false;
      video.controls = false;
      video.play().catch(() => closeVideo(details, true));
      details.querySelector('[data-maison-video-close]')?.focus({ preventScroll: true });
    }

    root.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const close = event.target.closest('[data-maison-video-close]');
      const summary = event.target.closest('.maisonco-pdp-hero__video-thumb');
      const details = (close || summary)?.closest('[data-maison-video]');
      if (!details) return;
      event.preventDefault();
      if (testimonialSwiper && !testimonialSwiper.allowClick) return;
      if (close || details.open) closeVideo(details, true);
      else openVideo(details);
    }, videoEventOptions);
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
      const details = event.target.closest('[data-maison-video][open]');
      if (details) { event.preventDefault(); closeVideo(details, true); }
    }, videoEventOptions);
    root.addEventListener('toggle', (event) => {
      if (event.target.matches('[data-maison-video]:not([open])')) event.target.querySelector('video')?.pause();
    }, { ...videoEventOptions, capture: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) root.querySelectorAll('[data-maison-video] video').forEach((video) => video.pause());
    }, videoEventOptions);

    const proofDialog = root.querySelector('[data-maison-proof-dialog]');
    root.querySelector('[data-maison-proof-open]')?.addEventListener('click', () => proofDialog?.showModal());
    root.querySelector('[data-maison-proof-close]')?.addEventListener('click', () => proofDialog?.close());
    proofDialog?.addEventListener('click', (event) => {
      if (event.target === proofDialog) proofDialog.close();
    });

    root.querySelectorAll('[data-maison-upsell-add]').forEach((button) => {
      button.addEventListener('click', async () => {
        if (button.disabled) return;
        button.disabled = true;
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: Number(button.dataset.maisonUpsellAdd), quantity: 1 }),
          });
          if (!response.ok) throw new Error(`Cart returned ${response.status}`);
          const drawer = document.querySelector('cart-drawer-component');
          const cart = drawer?.querySelector('kleone-cart');
          if (cart && typeof cart.refresh === 'function') {
            drawer.open();
            await cart.refresh();
          } else {
            window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
          }
        } catch (error) {
          console.error('Maison PDP Hero: cross sell add failed', error);
        } finally {
          button.disabled = false;
        }
      });
    });

    instances.set(root, {
      mainSwiper,
      thumbSwiper,
      testimonialSwiper,
      destroy() {
        leftColumnSize.disconnect();
        videoEvents.abort();
        videoVisibility.disconnect();
        root.querySelectorAll('[data-maison-video] video').forEach((video) => video.pause());
        if (proofDialog?.open) proofDialog.close();
        lightbox?.destroy();
        mainSwiper?.destroy(true, true);
        thumbSwiper?.destroy(true, true);
        testimonialSwiper?.destroy(true, true);
      },
    });
  }

  function destroy(root) {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-maison-pdp-hero]').forEach(init);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-maison-pdp-hero]').forEach(destroy);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const testimonial = event.target.closest('.maisonco-pdp-hero__testimonial');
    if (testimonial) {
      const root = testimonial.closest('[data-maison-pdp-hero]');
      instances.get(root)?.testimonialSwiper?.slideTo([...testimonial.parentElement.children].indexOf(testimonial));
      return;
    }
    const slide = event.target.closest('.maisonco-pdp-hero__slide');
    if (!slide) return;
    const root = slide.closest('[data-maison-pdp-hero]');
    const index = [...slide.parentElement.children].indexOf(slide);
    instances.get(root)?.mainSwiper?.slideTo(index);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
})();
